import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ERROR_MESSAGE, PAGE_ROUTES } from "../../constants";
import { getUrlSlug } from "../../helpers";
import useIntakeFormStore from "../../store/IntakeForm.store";
import { useSidebarStore } from "../../store/Sidebar.store";
import useUserStore from "../../store/User.store";

import Logo from "../../components/Logo";
import Modal from "../../components/Modal";
import Navigation from "../../components/Navigation";
import NavigationButton from "../../components/NavigationButton";
import Profile from "../../components/Profile";

import "./sidebar.scss";

const Sidebar = () => {
  //#region //! Page type
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const paths = getUrlSlug(pathname);
  const secondSlug = paths.length >= 2 && paths[1];
  const isCreationFormPage = secondSlug === "add";

  const getPageType = () => {
    if (paths.includes(PAGE_ROUTES.admin.baseRoute)) {
      return "admin";
    }
    if (pathname === PAGE_ROUTES.proposalListing.getFullPath()) {
      return "proposal-listing";
    }
    if (
      (paths.includes(PAGE_ROUTES.proposal.baseRoute) ||
        paths.includes(PAGE_ROUTES.duplicate.baseRoute)) &&
      paths.length > 1
    ) {
      return "proposal";
    }
    if (pathname === PAGE_ROUTES.clientQuestionnaireListing.getFullPath()) {
      return "client-questionnaire-listing";
    }
    if (
      paths.includes(PAGE_ROUTES.clientQuestionnaire.baseRoute) &&
      paths.length > 1
    ) {
      return "client-questionnaire";
    }

    return "generic";
  };
  const pageType = getPageType();

  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLeavePageModal, setShowLeavePageModal] = useState({
    show: false,
    path: "/",
    reload: false,
  });
  //#endregion

  //#region //! Stores
  const {
    data: { formState },
  } = useIntakeFormStore();
  const {
    data: { user },
    actions: { handleSignOut },
  } = useUserStore();
  const {
    data: { proposal },
  } = useSidebarStore();
  //#endregion

  //#region //! Handlers
  const handleSignOutUser = async () => {
    const res = await handleSignOut(user.username);

    // Redirect to Sign In if successful
    if (res.responseCode === 200) {
      useUserStore.persist.clearStorage();

      // remove user session proposals
      sessionStorage.removeItem("user-session-proposals");

      navigate("/signin");
    } else {
      setShowErrorModal(true);
    }
  };

  const handlePageNavigateWarning = (path = "/", reload = false) => {
    if (
      pageType === "proposal" &&
      isCreationFormPage &&
      !formState?.isFormLocked &&
      formState?.isFormDirty
    ) {
      setShowLeavePageModal({ show: true, path, reload });
      return;
    }

    if (isCreationFormPage) {
      setShowLeavePageModal({ show: true, path, reload });
    } else {
      navigate(path);
      reload && navigate(0);
    }
  };
  //#endregion

  const adminButton =
    user?.role === "admin" ? (
      <NavigationButton
        label="My Team"
        altLabel="My Team"
        variant="outline"
        collapsible
        href="/admin/users"
        iconName="users"
      />
    ) : null;

  const pageData = {
    proposal: proposal ? { ...proposal } : {},
  };

  return (
    <>
      <nav className="sidebar">
        <div className="sidebar__top-container">
          <header>
            <Logo onNavigateAway={handlePageNavigateWarning} />
          </header>

          <Navigation
            onNavigateAway={handlePageNavigateWarning}
            isAllspringTeam={true /*user.sponsor === REMI_TEAM_SPONSOR*/}
            pageData={pageData}
            pageType={pageType}
          />

          <div className="sidebar__bottom-container">
            <Profile {...user} />
            {adminButton}

            <NavigationButton
              label="Sign Out"
              altLabel="Sign Out"
              variant="outline"
              collapsible
              iconName="logout"
              onClick={() => setShowSignOutModal(true)}
            />
          </div>
          <a
            href="https://www.allspringglobal.com/"
            target="_blank"
            className="button button--link button--small button--left"
            rel="noreferrer"
          >
            Allspring Global Investments
          </a>
        </div>
      </nav>

      <Modal
        show={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleSignOutUser}
        heading="Sign Out?"
        subtitle="Are you sure you want to sign out?"
        cancelButton={{
          variant: "outline",
          children: "Cancel",
        }}
        confirmButton={{
          variant: "danger",
          children: "Sign Out",
        }}
      />

      <Modal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        onConfirm={null}
        heading="Error Signing Out"
        subtitle={ERROR_MESSAGE.SUBTITLE.TRY_AGAIN}
        cancelButton={{
          variant: "outline",
          children: "Retry Signout",
        }}
      />

      <Modal
        variation="basic"
        heading="Leave the Page?"
        subtitle="Are you sure you want to leave this page? The progress will not be saved."
        show={showLeavePageModal?.show}
        confirmButton={{
          variant: "danger",
          children: "Leave the Page",
        }}
        onClose={() => setShowLeavePageModal()}
        onConfirm={() => {
          setShowLeavePageModal();
          navigate(showLeavePageModal?.path);
          showLeavePageModal?.reload && navigate(0);
        }}
      />
    </>
  );
};

export default Sidebar;
